import clsx from 'clsx';
import type { Identifier, XYCoord } from 'dnd-core';
import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { DragItem, DragType } from '~components/generic/drag-and-drop/DragType';
import { Dropzone } from '~components/generic/drag-and-drop/Dropzone/Dropzone';
import Module from '~components/generic/module/Module';
import useAddModuleFromTemplate from '~hooks/useAddModuleFromTemplate';
import useMoveModule from '~hooks/useMoveModule';
import { IEntityComponentProps } from '~interfaces/props';

/**
 * Calculate if the mouse position is in the upper or lower half of the domNode
 * @return {boolean} `true` = above | `false` = below
 */
function calculateAboveOrBelow(domNode: HTMLElement, offset: XYCoord): boolean {
  // Determine rectangle on screen
  const hoverBoundingRect = domNode.getBoundingClientRect();
  // Get vertical middle
  const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
  // Get pixels to the top
  const hoverClientY = offset.y - hoverBoundingRect.top;
  // return comparison
  return hoverClientY < hoverMiddleY;
}

export const DraggableModule = (props: IEntityComponentProps) => {
  const { containerClassName = {}, type, index, entityId: id, parentContext, canMove } = props;
  const refDiv = useRef<HTMLDivElement>(null);
  const [insertAbove, setInsertAbove] = useState(true);

  const moveModule = useMoveModule({ parentId: parentContext, id });
  const addModule = useAddModuleFromTemplate({ parentId: parentContext });

  // Drop handler (Module) - This is for sorting the existing modules
  const [{ handlerId }, dropModule] = useDrop<DragItem, void, { handlerId: Identifier | null; isOver: boolean }>({
    accept: DragType.MODULE,
    collect: monitor => {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
    hover: (item, monitor) => {
      if (!refDiv.current) {
        return;
      }
      if (!monitor.canDrop()) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      const isAbove = calculateAboveOrBelow(refDiv.current, monitor.getClientOffset());
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && isAbove) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && !isAbove) {
        return;
      }

      // Time to actually perform the action
      moveModule(dragIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  // Drop handler (Template) - This is for dropping a template from the sidebar on this module (either above or below)
  const [{ isTemplateOver }, dropTemplate] = useDrop<DragItem, void, { isTemplateOver: boolean }>({
    accept: DragType.TEMPLATE,
    collect: monitor => ({
      isTemplateOver: monitor.canDrop() && monitor.isOver(),
    }),
    hover: (_item, monitor) => {
      if (!monitor.canDrop()) {
        return;
      }
      const isAbove = calculateAboveOrBelow(refDiv.current, monitor.getClientOffset());
      setInsertAbove(isAbove);
    },
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        addModule(item.entityType, insertAbove ? index : index + 1);
      }
      return { name: 'DraggableModule' };
    },
    canDrop: () => canMove,
  });

  // Drag handler - This is to make a module draggable to enable module sorting / re-sorting using drag-and-drop
  const [{ isDragging, canDrag }, dragModule] = useDrag<DragItem, unknown, { isDragging: boolean; canDrag: boolean }>({
    type: DragType.MODULE,
    item: () => {
      return { entityType: type, id, index };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
      canDrag: monitor.canDrag(),
    }),
    canDrag: () => canMove,
  });

  // We wrap the DIV ref with both our drop handlers and with the drag handler
  if (canMove) {
    dragModule(dropModule(dropTemplate(refDiv)));
  }

  const classes = clsx(
    containerClassName,
    `m-c-${type as string}`,
    isDragging && 'opacity-0',
    isDragging && 'cursor-grabbing',
    canDrag && !isDragging && 'cursor-grab',
  );

  if (canMove) {
    // Render draggable version
    return (
      <div ref={refDiv} className={classes} data-handler-id={handlerId}>
        {isTemplateOver && insertAbove && <Dropzone canDrop isOver />}
        <Module {...props} />
        {isTemplateOver && !insertAbove && <Dropzone canDrop isOver />}
      </div>
    );
  } else {
    // Render non-draggable version
    return (
      <div className={classes}>
        <Module {...props} />
      </div>
    );
  }
};
